import { z } from "zod";
import { assertUniqueSortOrders, nlWkrCategorySchema } from "./nlWkr.js";
/**
 * Schemas for the expense write routes (apps/api/src/routes/accounting/expenses.ts).
 *
 * Wrap the create_expense_with_line_items / update_expense_with_line_items /
 * delete_expense RPCs and the expense↔ledger project sync. These RPCs write to
 * the ledger, so the frontend must route them through the backend (HMAC) before
 * the RESTRICTIVE verify_backend_token() ledger policy lands. See MED-1
 * (SCRUM-184) and HIGH-1/HIGH-2 (SCRUM-181/182).
 */
const expenseLineItemSchema = z
    .object({
    expense_account_id: z.string().uuid(),
    description: z.string(),
    quantity: z.number(),
    unit_price: z.number(),
    tax_rate: z.number(),
    tax_rate_id: z.string().uuid().optional(),
    sort_order: z.number().int(),
    tax_treatment: z.string().optional(),
    tax_inclusive: z.boolean().optional(),
    exemption_reason: z.string().optional(),
    exemption_reason_other: z.string().optional(),
    /** NL WKR classification (jurisdiction-gated in the UI; rides through to nl_wkr_sync_from_lines). */
    wkr_category: nlWkrCategorySchema.optional(),
})
    .strict();
// Shared body for create + update (expense_id lives in the URL for update).
const expenseBodyShape = {
    business_id: z.string().uuid().nullable().optional(),
    person_id: z.string().uuid().nullable().optional(),
    custom_business_name: z.string().nullable().optional(),
    // `expenses.expense_date` is a plain `date` column and the RPC param is text.
    // TRANSITION (one release): the datetime-local form of this field is still
    // accepted so a stale web tab open across the deploy keeps working —
    // `'2026-03-15T14:30'::date` casts fine in SQL. Tighten to date-only
    // (`/^\d{4}-\d{2}-\d{2}$/`) next release.
    expense_date: z.string().regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?$/, {
        message: "Expected yyyy-MM-dd or yyyy-MM-dd'T'HH:mm",
    }),
    // Optional in the UI (the expense form's "memo" field sends '' when blank).
    // Keep it a string rather than nullable: the RPC writes it into
    // ledger_transactions.description, which is NOT NULL.
    description: z.string().default(""),
    payment_account_id: z.string().uuid(),
    reference: z.string().nullable().optional(),
    project_id: z.string().uuid().nullable().optional(),
    fund_id: z.string().uuid().nullable().optional(),
    line_items: z
        .array(expenseLineItemSchema)
        .min(1)
        .superRefine(assertUniqueSortOrders),
};
export const createExpenseSchema = z.object(expenseBodyShape).strict();
export const updateExpenseSchema = z.object(expenseBodyShape).strict();
/**
 * Header-only save for a DECLARED expense
 * (PATCH /orgs/:orgId/expenses/:expenseId/header → `update_expense_header`).
 *
 * Once an expense's VAT lines sit on a filed return, the full update RPC is
 * refused (it deletes + re-posts every ledger line). This body is the subset
 * that never touches a line: payee, description, project, fund, reference.
 * `.strict()` is the frozen-field fence — `expense_date`,
 * `payment_account_id` and `line_items` are rejected outright rather than
 * silently dropped. See docs/TAX.md "Edit guard".
 *
 * The expense form routes here only for a declared expense; the expenses
 * grid's staged Payee edit also uses it, for ANY expense (the payee is a
 * header field, so no line needs re-posting).
 */
export const updateExpenseHeaderSchema = z
    .object(expenseBodyShape)
    .pick({
    business_id: true,
    person_id: true,
    custom_business_name: true,
    description: true,
    project_id: true,
    fund_id: true,
    reference: true,
})
    .strict();
// ---------- bulk delete (the expenses grid's bulk actions) ----------
/**
 * Max expense ids per POST /api/v1/expenses/orgs/:orgId/expenses/batch-delete.
 * The web queues the selection and sends it in requests of at most this many;
 * the body schema caps `expense_ids` at the same number (the bank feed's
 * `CLEARING_BATCH_MAX_OPS` precedent).
 */
export const EXPENSE_BATCH_MAX_IDS = 50;
/**
 * Request body for the batch delete. Each id runs the same `delete_expense`
 * RPC as the single-row DELETE route, through the same helpers, in input
 * order. Duplicates are refused (a second delete of the same row would only
 * ever report `not_found`, which reads as a failure).
 */
export const expenseBatchDeleteSchema = z
    .object({
    expense_ids: z
        .array(z.string().uuid())
        .min(1)
        .max(EXPENSE_BATCH_MAX_IDS)
        .refine((ids) => new Set(ids).size === ids.length, {
        message: "expense_ids must be unique",
    }),
})
    .strict();
//# sourceMappingURL=expenses.js.map